/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

/**
 * A codec for encoding and decoding parameters in URLs.
 *
 * Used by `HttpParams`.
 *
 *  @experimental
 **/
export interface HttpParameterCodec {
  encodeKey(key: string): string;
  encodeValue(value: string): string;

  decodeKey(key: string): string;
  decodeValue(value: string): string;
}
/**
 * A `HttpParameterCodec` that uses `encodeURIComponent` and `decodeURIComponent` to
 * serialize and parse URL parameter keys and values.
 * 
 * \@experimental
 */
export class HttpUrlEncodingCodec implements HttpParameterCodec {
/**
 * @param {?} k
 * @return {?}
 */
encodeKey(k: string): string { return standardEncoding(k); }
/**
 * @param {?} v
 * @return {?}
 */
encodeValue(v: string): string { return standardEncoding(v); }
/**
 * @param {?} k
 * @return {?}
 */
decodeKey(k: string): string { return decodeURIComponent(k); }
/**
 * @param {?} v
 * @return {?}
 */
decodeValue(v: string) { return decodeURIComponent(v); }
}
/**
 * @param {?} rawParams
 * @param {?} codec
 * @return {?}
 */
function paramParser(rawParams: string, codec: HttpParameterCodec): Map<string, string[]> {
  const /** @type {?} */ map = new Map<string, string[]>();
  if (rawParams.length > 0) {
    const /** @type {?} */ params: string[] = rawParams.split('&');
    params.forEach((param: string) => {
      const /** @type {?} */ eqIdx = param.indexOf('=');
      const [key, val]: string[] = eqIdx == -1 ?
          [codec.decodeKey(param), ''] :
          [codec.decodeKey(param.slice(0, eqIdx)), codec.decodeValue(param.slice(eqIdx + 1))];
      const /** @type {?} */ list = map.get(key) || [];
      list.push(val);
      map.set(key, list);
    });
  }
  return map;
}
/**
 * @param {?} v
 * @return {?}
 */
function standardEncoding(v: string): string {
  return encodeURIComponent(v)
      .replace(/%40/gi, '@')
      .replace(/%3A/gi, ':')
      .replace(/%24/gi, '$')
      .replace(/%2C/gi, ',')
      .replace(/%3B/gi, ';')
      .replace(/%2B/gi, '+')
      .replace(/%3D/gi, '=')
      .replace(/%3F/gi, '?')
      .replace(/%2F/gi, '/');
}

interface Update {
  param: string;
  value?: string;
  op: 'a'|'d'|'s';
}
/**
 * An HTTP request/response body that represents serialized parameters,
 * per the MIME type `application/x-www-form-urlencoded`.
 * 
 * This class is immuatable - all mutation operations return a new instance.
 * 
 * \@experimental
 */
export class HttpParams {
private map: Map<string, string[]>|null;
private encoder: HttpParameterCodec;
private updates: Update[]|null = null;
private cloneFrom: HttpParams|null = null;
/**
 * @param {?=} options
 */
constructor(options: {
    fromString?: string,
    encoder?: HttpParameterCodec,
  } = {}) {
    this.encoder = options.encoder || new HttpUrlEncodingCodec();
    this.map = !!options.fromString ? paramParser(options.fromString, this.encoder) : null;
  }
/**
 * Check whether the body has one or more values for the given parameter name.
 * @param {?} param
 * @return {?}
 */
has(param: string): boolean {
    this.init();
    return /** @type {?} */(( this.map)).has(param);
  }
/**
 * Get the first value for the given parameter name, or `null` if it's not present.
 * @param {?} param
 * @return {?}
 */
get(param: string): string|null {
    this.init();
    const /** @type {?} */ res = /** @type {?} */(( this.map)).get(param);
    return !!res ? res[0] : null;
  }
/**
 * Get all values for the given parameter name, or `null` if it's not present.
 * @param {?} param
 * @return {?}
 */
getAll(param: string): string[]|null {
    this.init();
    return /** @type {?} */(( this.map)).get(param) || null;
  }
/**
 * Get all the parameter names for this body.
 * @return {?}
 */
keys(): string[] {
    this.init();
    return Array.from( /** @type {?} */((this.map)).keys());
  }
/**
 * Construct a new body with an appended value for the given parameter name.
 * @param {?} param
 * @param {?} value
 * @return {?}
 */
append(param: string, value: string): HttpParams { return this.clone({param, value, op: 'a'}); }
/**
 * Construct a new body with a new value for the given parameter name.
 * @param {?} param
 * @param {?} value
 * @return {?}
 */
set(param: string, value: string): HttpParams { return this.clone({param, value, op: 's'}); }
/**
 * Construct a new body with either the given value for the given parameter
 * removed, if a value is given, or all values for the given parameter removed
 * if not.
 * @param {?} param
 * @param {?=} value
 * @return {?}
 */
delete (param: string, value?: string): HttpParams { return this.clone({param, value, op: 'd'}); }
/**
 * Serialize the body to an encoded string, where key-value pairs (separated by `=`) are
 * separated by `&`s.
 * @return {?}
 */
toString(): string {
    this.init();
    return this.keys()
        .map(key => {
          const /** @type {?} */ eKey = this.encoder.encodeKey(key);
          return /** @type {?} */(( /** @type {?} */(( this.map)).get(key))).map(value => eKey + '=' + this.encoder.encodeValue(value))
              .join('&');
        })
        .join('&');
  }
/**
 * @param {?} update
 * @return {?}
 */
private clone(update: Update): HttpParams {
    const /** @type {?} */ clone = new HttpParams({encoder: this.encoder});
    clone.cloneFrom = this.cloneFrom || this;
    clone.updates = (this.updates || []).concat([update]);
    return clone;
  }
/**
 * @return {?}
 */
private init() {
    if (this.map === null) {
      this.map = new Map<string, string[]>();
    }
    if (this.cloneFrom !== null) {
      this.cloneFrom.init();
      this.cloneFrom.keys().forEach(key => /** @type {?} */(( this.map)).set(key, /** @type {?} */(( /** @type {?} */(( /** @type {?} */(( this.cloneFrom)).map)).get(key))))); /** @type {?} */((
      this.updates)).forEach(update => {
        switch (update.op) {
          case 'a':
          case 's':
            const /** @type {?} */ base = (update.op === 'a' ? /** @type {?} */(( this.map)).get(update.param) : undefined) || [];
            base.push( /** @type {?} */((update.value))); /** @type {?} */((
            this.map)).set(update.param, base);
            break;
          case 'd':
            if (update.value !== undefined) {
              let /** @type {?} */ base = /** @type {?} */(( this.map)).get(update.param) || [];
              const /** @type {?} */ idx = base.indexOf(update.value);
              if (idx !== -1) {
                base.splice(idx, 1);
              }
              if (base.length > 0) { /** @type {?} */((
                this.map)).set(update.param, base);
              } else { /** @type {?} */((
                this.map)).delete(update.param);
              }
            } else { /** @type {?} */((
              this.map)).delete(update.param);
              break;
            }
        }
      });
      this.cloneFrom = null;
    }
  }
}

function HttpParams_tsickle_Closure_declarations() {
/** @type {?} */
HttpParams.prototype.map;
/** @type {?} */
HttpParams.prototype.encoder;
/** @type {?} */
HttpParams.prototype.updates;
/** @type {?} */
HttpParams.prototype.cloneFrom;
}




// WEBPACK FOOTER //
// ../../../../../packages/common/http/src/params.ts